/**
 * ShiftCard Component - Displays a single shift in the calendar
 */

import React from 'react';
import { Badge } from '@/components/ui/badge';

// Role colors for visual distinction
const ROLE_COLORS = {
  server: { bg: 'bg-blue-500/20', border: 'border-blue-500', text: 'text-blue-400' },
  cook: { bg: 'bg-orange-500/20', border: 'border-orange-500', text: 'text-orange-400' },
  chef: { bg: 'bg-orange-500/20', border: 'border-orange-500', text: 'text-orange-400' },
  host: { bg: 'bg-purple-500/20', border: 'border-purple-500', text: 'text-purple-400' },
  manager: { bg: 'bg-emerald-500/20', border: 'border-emerald-500', text: 'text-emerald-400' },
  driver: { bg: 'bg-cyan-500/20', border: 'border-cyan-500', text: 'text-cyan-400' },
  cashier: { bg: 'bg-pink-500/20', border: 'border-pink-500', text: 'text-pink-400' },
  default: { bg: 'bg-slate-500/20', border: 'border-slate-500', text: 'text-slate-400' },
};

// Status badges
const STATUS_BADGES = {
  DRAFT: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Draft' },
  PUBLISHED: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Published' },
  CANCELED: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Canceled' },
};

export const getRoleColor = (role) => {
  const normalizedRole = (role || '').toLowerCase();
  return ROLE_COLORS[normalizedRole] || ROLE_COLORS.default;
};

export default function ShiftCard({ shift, onClick, compact = false }) {
  const roleColor = getRoleColor(shift.role_name);
  const startTime = shift.start_at?.split('T')[1]?.substring(0, 5) || '';
  const endTime = shift.end_at?.split('T')[1]?.substring(0, 5) || '';
  const statusBadge = STATUS_BADGES[shift.status] || STATUS_BADGES.DRAFT;

  if (compact) {
    return (
      <div
        className={`p-1.5 sm:p-2 rounded border-l-2 ${roleColor.bg} ${roleColor.border} cursor-pointer hover:opacity-80 transition-opacity relative`}
        onClick={() => onClick?.(shift)}
        data-testid={`shift-card-${shift.id}`}
      >
        {/* Status indicator */}
        <div 
          className={`absolute top-1 right-1 w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${shift.status === 'PUBLISHED' ? 'bg-emerald-400' : 'bg-yellow-400'}`} 
          title={statusBadge.label} 
        />
        
        <p className="text-[10px] sm:text-xs font-medium text-white truncate pr-3">
          {shift.employee_name || 'Unassigned'}
        </p>
        {shift.title && (
          <p className="text-[8px] sm:text-[10px] text-slate-400 truncate">{shift.title}</p>
        )}
        <p className={`text-[10px] sm:text-xs ${roleColor.text}`}>
          {startTime} - {endTime}
        </p>
        <div className="hidden sm:flex items-center gap-1 mt-1 flex-wrap">
          {shift.station && (
            <Badge variant="outline" className="text-[10px] text-cyan-400 border-cyan-400/50 px-1 py-0">
              {shift.station}
            </Badge>
          )}
          {shift.role_name && (
            <Badge variant="outline" className={`text-[10px] ${roleColor.text} border-current px-1 py-0`}>
              {shift.role_name}
            </Badge>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`p-2 sm:p-3 rounded-lg border-l-4 ${roleColor.bg} ${roleColor.border} cursor-pointer hover:opacity-80 transition-opacity relative`}
      onClick={() => onClick?.(shift)}
      data-testid={`shift-card-${shift.id}`}
    >
      {/* Status indicator */}
      <div 
        className={`absolute top-2 right-2 w-2 h-2 rounded-full ${shift.status === 'PUBLISHED' ? 'bg-emerald-400' : 'bg-yellow-400'}`} 
        title={statusBadge.label} 
      />
      
      <p className="text-xs sm:text-sm font-medium text-white truncate pr-4">
        {shift.employee_name || 'Unassigned'}
      </p>
      {shift.title && (
        <p className="text-[10px] sm:text-xs text-slate-400 truncate mt-0.5">{shift.title}</p>
      )}
      <p className={`text-xs sm:text-sm ${roleColor.text} mt-1`}>
        {startTime} - {endTime}
      </p>
      <div className="flex items-center gap-1 mt-2 flex-wrap">
        {shift.station && (
          <Badge variant="outline" className="text-[10px] sm:text-xs text-cyan-400 border-cyan-400/50">
            {shift.station}
          </Badge>
        )}
        {shift.role_name && (
          <Badge variant="outline" className={`text-[10px] sm:text-xs ${roleColor.text} border-current`}>
            {shift.role_name}
          </Badge>
        )}
      </div>
    </div>
  );
}

export { ROLE_COLORS, STATUS_BADGES };
